import React, { useEffect, useRef, useState } from 'react';
import { copyTextToClipboard } from '../../../utils/fundingClipboardUtils';
import { formatProxyLaunchTokenCountdown } from '../../../services/funding/fundingAuthorizedQaProxyService';
import {
  TERMINAL_TARGET_UNIX,
  TERMINAL_TARGET_WINDOWS_CMD,
  TERMINAL_TARGET_WINDOWS_POWERSHELL,
} from '../../../services/funding/fundingBrowserProfilesService';

function terminalLabel(terminal) {
  if (terminal === TERMINAL_TARGET_WINDOWS_POWERSHELL) return 'Windows PowerShell';
  if (terminal === TERMINAL_TARGET_WINDOWS_CMD) return 'Windows Command Prompt';
  return 'macOS/Linux Terminal';
}

function isExpiredAt(expiresAt, nowMs) {
  const expiresMs = Date.parse(expiresAt || '');
  return !Number.isNaN(expiresMs) && expiresMs <= nowMs;
}

/**
 * Short-lived QA session launch. The token only exists inside the copied command and is
 * dropped by the parent (onExpire) as soon as it expires — a fresh token never needs a reinstall.
 */
export default function AuthorizedQaProxyLaunchTokenModal({
  open,
  session,
  autoCopySucceeded = false,
  generating = false,
  error = '',
  onGenerateNewToken,
  onRunConnectionTest,
  onChangeLocation,
  onInstallUpdate,
  onExpire,
  onClose,
}) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [copyNotice, setCopyNotice] = useState('');
  const expiredNotifiedRef = useRef('');

  useEffect(() => {
    setCopyNotice('');
    if (!open) return undefined;
    setNowMs(Date.now());
    const timer = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [open, session?.issuedAt, session?.expiresAt]);

  const expired = Boolean(
    session && (session.expired || isExpiredAt(session.expiresAt, nowMs)),
  );

  useEffect(() => {
    if (!open || !session?.expiresAt || session.expired) return undefined;
    const notify = () => {
      if (expiredNotifiedRef.current === session.expiresAt) return;
      expiredNotifiedRef.current = session.expiresAt;
      onExpire?.();
    };
    const remaining = Date.parse(session.expiresAt) - Date.now();
    if (Number.isNaN(remaining)) return undefined;
    if (remaining <= 0) {
      notify();
      return undefined;
    }
    const timer = window.setTimeout(notify, remaining + 25);
    return () => window.clearTimeout(timer);
  }, [open, session?.expiresAt, session?.expired, onExpire]);

  if (!open || !session) return null;

  if (session.blocked) {
    return (
      <div
        className="fg-modal-backdrop is-open"
        data-testid="authorized-qa-proxy-launch-token-modal"
        data-launch-state="blocked"
      >
        <div className="fg-modal fg-browser-profile-modal" role="dialog" aria-modal="true">
          <button
            type="button"
            className="fg-modal-close"
            onClick={() => onClose?.({ reason: 'closed' })}
            aria-label="Close"
            data-testid="authorized-qa-proxy-launch-token-close"
          >
            ×
          </button>
          <div className="fg-modal-header">
            <div className="fg-modal-icon">QA</div>
            <div>
              <h2>QA Session can&apos;t start yet</h2>
              <p data-testid="authorized-qa-proxy-launch-token-help">
                {`QA location: ${session.locationSummary || 'active QA location'}`}
              </p>
              {session.verificationSummary ? (
                <p className="fg-field-hint" data-testid="authorized-qa-proxy-launch-verification">
                  {`Connection verification: ${session.verificationSummary}`}
                </p>
              ) : null}
            </div>
          </div>
          <div className="fg-profile-form">
            <p className="fg-form-error" data-testid="authorized-qa-proxy-launch-blocked-reason">
              {session.message || 'QA Session can\u2019t start yet.'}
            </p>
            <div className="fg-browser-proxy-token-actions">
              {session.updateRequired && onInstallUpdate ? (
                <button
                  type="button"
                  className="fg-btn fg-btn-primary"
                  data-testid="authorized-qa-proxy-launch-blocked-install"
                  onClick={() => onInstallUpdate()}
                >
                  Install / Update QA Browser
                </button>
              ) : null}
              {onRunConnectionTest ? (
                <button
                  type="button"
                  className="fg-btn fg-btn-light"
                  data-testid="authorized-qa-proxy-launch-blocked-test"
                  onClick={() => onRunConnectionTest()}
                >
                  Run Connection Test
                </button>
              ) : null}
              {onChangeLocation ? (
                <button
                  type="button"
                  className="fg-btn fg-btn-light"
                  data-testid="authorized-qa-proxy-launch-blocked-change-location"
                  onClick={() => onChangeLocation()}
                >
                  Change QA Location
                </button>
              ) : null}
              <button
                type="button"
                className="fg-btn fg-btn-light"
                data-testid="authorized-qa-proxy-launch-token-done"
                onClick={() => onClose?.({ reason: 'closed' })}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const runCommand = expired ? '' : String(session.runCommand || '');
  const terminal = session.terminal || TERMINAL_TARGET_UNIX;
  const countdown = formatProxyLaunchTokenCountdown(session.expiresAt, nowMs);

  const handleCopy = async () => {
    try {
      await copyTextToClipboard(runCommand);
      setCopyNotice('Launch command copied. Paste it into your terminal.');
    } catch {
      setCopyNotice('Unable to copy. Select the command below and copy it manually.');
    }
  };

  let statusText = copyNotice
    || (autoCopySucceeded ? 'Launch command copied. Paste it into your terminal.' : 'Copy the launch command and paste it into your terminal.');
  if (expired) statusText = 'Generate a new token to launch again. No reinstall is needed.';

  return (
    <div
      className="fg-modal-backdrop is-open"
      data-testid="authorized-qa-proxy-launch-token-modal"
    >
      <div className="fg-modal fg-browser-profile-modal" role="dialog" aria-modal="true">
        <button
          type="button"
          className="fg-modal-close"
          onClick={() => onClose?.({ reason: 'closed' })}
          aria-label="Close"
          data-testid="authorized-qa-proxy-launch-token-close"
        >
          ×
        </button>
        <div className="fg-modal-header">
          <div className="fg-modal-icon">QA</div>
          <div>
            <h2>Authorized QA Session</h2>
            <p data-testid="authorized-qa-proxy-launch-token-help">
              {`QA location: ${session.locationSummary || 'active QA location'}`}
            </p>
            {session.verificationSummary ? (
              <p className="fg-field-hint" data-testid="authorized-qa-proxy-launch-verification">
                {`Connection verification: ${session.verificationSummary}`}
              </p>
            ) : null}
            <p className="fg-field-hint" data-testid="authorized-qa-proxy-launch-token-scope">
              Short-lived and single-use. Opens the QA Browser installed on this computer
              through the QA proxy — never Primary Browser.
            </p>
          </div>
        </div>

        <div className="fg-profile-form">
          <p
            className={expired ? 'fg-form-error' : 'fg-field-hint'}
            data-testid="authorized-qa-proxy-launch-token-expiry"
          >
            {expired ? 'Token expired' : `Token expires in ${countdown}`}
            {` · ${terminalLabel(terminal)}`}
          </p>
          <p
            className="fg-field-hint"
            data-testid="authorized-qa-proxy-launch-token-copy-status"
          >
            {statusText}
          </p>
          {error ? (
            <p className="fg-form-error" data-testid="authorized-qa-proxy-launch-token-error">
              {error}
            </p>
          ) : null}

          {!expired && runCommand ? (
            <details className="fg-browser-command-block">
              <summary>Show launch command</summary>
              <pre
                className="fg-browser-command-block__code"
                data-testid="authorized-qa-proxy-run-command"
              >
                <code>{runCommand}</code>
              </pre>
            </details>
          ) : null}

          <div className="fg-browser-proxy-token-actions">
            {expired ? (
              <button
                type="button"
                className="fg-btn fg-btn-primary"
                data-testid="authorized-qa-proxy-generate-new-token"
                disabled={generating || !onGenerateNewToken}
                onClick={() => onGenerateNewToken?.()}
              >
                {generating ? 'Generating…' : 'Generate New Token'}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="fg-btn fg-btn-primary"
                  data-testid="authorized-qa-proxy-copy-run-command"
                  disabled={!runCommand}
                  onClick={handleCopy}
                >
                  Copy Launch Command
                </button>
                {onGenerateNewToken ? (
                  <button
                    type="button"
                    className="fg-btn fg-btn-light"
                    data-testid="authorized-qa-proxy-generate-new-token"
                    disabled={generating}
                    onClick={() => onGenerateNewToken()}
                  >
                    {generating ? 'Generating…' : 'Generate New Token'}
                  </button>
                ) : null}
              </>
            )}
            <button
              type="button"
              className="fg-btn fg-btn-light"
              data-testid="authorized-qa-proxy-launch-token-done"
              onClick={() => onClose?.({ reason: 'closed' })}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
